import { TPropsWithChildren } from "../types/util";
import React, {
  createContext,
  createRef,
  Dispatch,
  SetStateAction,
  useContext,
  useRef,
  useState,
} from "react";
import Player from "video.js/dist/types/player";

// https://blog.csdn.net/Q147351/article/details/106663908
enum VideoStatusEnum {
  ready = "ready",
  play = "play",
  playing = "playing",
  ended = "ended",
  notready = "notready",
  loadstart = "loadstart",
  waiting = "waiting",
  progress = "progress",
  canplaythrough = "canplaythrough",
}

type TVideoContext = {
  playerRef: React.MutableRefObject<Player | null>;
  play?: (src: string) => void;
  status: VideoStatusEnum;
  setStatus: Dispatch<SetStateAction<VideoStatusEnum>>;
  playerTimer: NodeJS.Timeout | null;
  setPlayerTimer: (pre: NodeJS.Timeout | null) => void;
};

type TProps = TPropsWithChildren;

const VideoContext = createContext<TVideoContext>({
  playerRef: createRef(),
  status: VideoStatusEnum.notready,
  setStatus: () => {},
  playerTimer: null,
  setPlayerTimer: () => {},
});

const VideoProvider = (props: TProps) => {
  const playerRef = useRef<Player | null>(null);
  const [status, setStatus] = useState(VideoStatusEnum.notready);
  const [playerTimer, setPlayerTimer] = useState<NodeJS.Timeout | null>(null);

  const play = (src: string) => {
    playerRef.current?.src({
      src,
      type: "video/webm",
    });

    playerRef.current?.poster(process.env.PUBLIC_URL + "/images/poster.png");
    playerRef.current?.load();
    const playPromise = playerRef.current?.play();
    if (playPromise) {
      playPromise.catch(() => {});
    }
  };

  return (
    <VideoContext.Provider
      value={{
        playerRef,
        play,
        status,
        setStatus,
        playerTimer,
        setPlayerTimer,
      }}
    >
      {props.children}
    </VideoContext.Provider>
  );
};

const useVideoContext = () => useContext(VideoContext);

export { VideoProvider, useVideoContext, VideoStatusEnum };
